import { ErrorOutline } from "@mui/icons-material";
import { styled, Typography } from "@mui/material";
import type { UseQueryResult } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { Card } from "~/components/Card";
import { Details } from "~/components/Details";
import {
  DetailsLayout,
  HistoryCard,
  LockCard,
  ManageCard,
  ResourceFields,
} from "~/components/DetailsCards";
import { JsonField } from "~/components/DetailsCards/JsonField";
import {
  Form,
  FormSkeleton,
  getChangedFields,
  ObjectField,
  TextField,
  useStudioForm,
} from "~/components/Form";
import { QueryRenderer } from "~/components/QueryRenderer";
import {
  useDeleteRecord,
  useRecord,
  useUpdateRecord,
} from "~/domain/datastores";
import { pick } from "~/lib/std";
import {
  makeEditRecordLocation,
  makeRecordsLocation,
  useRecordParams,
} from "~/paths";
import type { Record } from "~/services/datastore";
import { selectData } from "~/utils";
import { editRecordSchema } from "../schemas";

export function RecordDetails() {
  const { topicId, timestamp } = useRecordParams();

  const query = useRecord(topicId, timestamp, { select: selectData });

  const generalSection = <GeneralSection query={query} />;
  const dataSection = <DataSection query={query} />;
  const infoSection = <InfoSection query={query} />;
  const lockSection = (
    <LockCard
      resourceName="record"
      query={query}
      updateMutation={useUpdateRecord(topicId, timestamp)}
    />
  );
  const historySection = <HistoryCard query={query} />;
  const manageSection = (
    <ManageCard
      resourceName="record"
      query={query}
      editLocation={makeEditRecordLocation({ topicId, timestamp })}
      deleteMutation={useDeleteRecord(topicId, timestamp)}
      getReadableName={(record) => String(record.timestamp)}
      listLocation={makeRecordsLocation({ topicId })}
    />
  );

  return (
    <DetailsLayout
      primaryGridColumn={
        <>
          {generalSection}
          {dataSection}
        </>
      }
      secondaryGridColumn={
        <>
          {infoSection}
          {lockSection}
          {historySection}
          {manageSection}
        </>
      }
      stack={
        <>
          {generalSection}
          {infoSection}
          {dataSection}
          {lockSection}
          {historySection}
          {manageSection}
        </>
      }
    />
  );
}

function GeneralSection({ query }: { query: UseQueryResult<Record> }) {
  return (
    <Card title="General">
      <ResourceFields
        query={query}
        fields={[
          { dataType: "id", accessor: "timestamp" },
          { dataType: "topic", accessor: "topicId" },
          { dataType: "log", accessor: "logId" },
          { dataType: "ingestion", accessor: "ingestionId" },
          { dataType: "bytes", accessor: "dataOffset" },
          { dataType: "bytes", accessor: "dataLength" },
          { dataType: "text", accessor: "chunkCompression" },
          { dataType: "bytes", accessor: "chunkOffset" },
          { dataType: "bytes", accessor: "chunkLength" },
          { dataType: "text", accessor: "source" },
        ]}
      />
    </Card>
  );
}

const ErrorContent = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateAreas: `
    "icon message"
    "icon error"
  `,
  columnGap: theme.spacing(2),
  gridTemplateColumns: "auto minmax(0, 1fr)",
}));

function DataSection({ query }: { query: UseQueryResult<Record> }) {
  const hasError = query.data?.error != null;

  return (
    <Card title="Data" error={hasError}>
      {hasError ? (
        <ErrorContent>
          <ErrorOutline
            sx={{
              gridArea: "icon",
              fontSize: "3rem",
            }}
            color="error"
          />
          <Typography paragraph sx={{ gridArea: "message" }}>
            The record failed to process
          </Typography>
          <Details sx={{ gridArea: "error" }}>
            <Details.Summary>Expand to see error</Details.Summary>
            <JsonField value={query.data!.error} />
          </Details>
        </ErrorContent>
      ) : (
        <ResourceFields
          query={query}
          fields={[{ dataType: "json", accessor: "queryData" }]}
        />
      )}
    </Card>
  );
}

function InfoSection({ query }: { query: UseQueryResult<Record> }) {
  return (
    <Card>
      <QueryRenderer
        query={query}
        loading={<FormSkeleton shapes={["multiline", "multiline"]} />}
        success={(record) => <InfoSectionImpl record={record} />}
      />
    </Card>
  );
}

function InfoSectionImpl({ record }: { record: Record }) {
  const updateRecord = useUpdateRecord(record.topicId, record.timestamp);

  const { enqueueSnackbar } = useSnackbar();

  const schema = editRecordSchema.pick({ note: true, context: true });
  const FIELDS = schema.keyof().options;

  const {
    control,
    handleSubmit,
    formState: { dirtyFields },
    reset,
  } = useStudioForm({
    schema,
    defaultValues: pick(record, FIELDS),
    onSubmit(values) {
      const changedFields = getChangedFields(values, dirtyFields);

      updateRecord.mutate(changedFields, {
        onSuccess(response) {
          enqueueSnackbar("Record updated", { variant: "success" });

          reset(pick(response.data, FIELDS));
        },
        onError() {
          enqueueSnackbar("Unable to update record", { variant: "error" });
        },
      });
    },
  });

  return (
    <>
      <Form
        onSubmit={handleSubmit}
        loading={updateRecord.isLoading}
        submitText="Save Changes"
        disabled={record.locked}
      >
        <TextField control={control} name="note" multiline />
        <ObjectField control={control} name="context" />
      </Form>
      {record.locked && (
        <Typography variant="body2">
          Unlock this record to make updates.
        </Typography>
      )}
    </>
  );
}
